/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SEARCH_SPLIT_URL_STATE_V2
 * Date:         2025-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Extracted URL and initial state logic from useSearchState.ts (2025-12-30).
 * V2: Fixed getInitialFilters useCallback to properly memoize with location.search.
 *
 * File Size Check:
 * - Current: 63 lines
 * - Modifying: 1 line
 * - Projected: 63 lines
 * - Decision: Proceed (under 150 line limit)
 */

import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useURLHelpers } from "../utils/urlHelpers";
import { TabType } from "../types";
import { InventoryCategory, InventoryCondition } from "../../../interfaces/Inventory";

export const useSearchURLState = () => {
  const location = useLocation();
  const { getFilterFromURL } = useURLHelpers();

  const getInitialTab = (): TabType => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get("tab");
    if (tab === "inventory" || tab === "companies") return tab;
    return "broadcasts";
  };

  const getInitialFilters = useCallback(() => {
    return {
      inventoryFilterKeyword: getFilterFromURL("inv_keyword", "") as string,
      inventoryPartNumber: getFilterFromURL("inv_part", "") as string,
      category: getFilterFromURL("inv_category", "") as InventoryCategory | "",
      condition: getFilterFromURL("inv_condition", "") as InventoryCondition | "",
      manufacturer: getFilterFromURL("inv_manufacturer", "") as string,
      country: getFilterFromURL("inv_country", "") as string,
      company: getFilterFromURL("inv_company", "") as string,
      minQty: getFilterFromURL("inv_qty", 0) as number,
      maxQty: 0,
      sort: getFilterFromURL("sort", "newest") as "newest" | "oldest" | "price-asc" | "price-desc",
      inventoryPage: getFilterFromURL("inv_page", 1) as number,
      inventoryItemsPerPage: getFilterFromURL("inv_per_page", 10) as number,
      broadcastFilterKeyword: getFilterFromURL("bc_keyword", "") as string,
      broadcastSKU: getFilterFromURL("bc_sku", "") as string,
      broadcastType: getFilterFromURL("bc_type", "") as string,
      broadcastManufacturer: getFilterFromURL("bc_manufacturer", "") as string,
      broadcastCategory: getFilterFromURL("bc_category", "") as string,
      broadcastQty: getFilterFromURL("bc_qty", 0) as number,
      broadcastCondition: getFilterFromURL("bc_condition", "") as string,
      broadcastCountry: getFilterFromURL("bc_country", "") as string,
      broadcastCompany: getFilterFromURL("bc_company", "") as string,
      broadcastPage: getFilterFromURL("bc_page", 1) as number,
      broadcastItemsPerPage: getFilterFromURL("bc_per_page", 10) as number,
      companyFilterKeyword: getFilterFromURL("comp_keyword", "") as string,
      companyCountryFilter: getFilterFromURL("comp_country", "") as string,
      companyPage: getFilterFromURL("comp_page", 1) as number,
      companyItemsPerPage: getFilterFromURL("comp_per_page", 10) as number,
    };
  }, [getFilterFromURL]); // [2025-01-01] ESLint fix: Removed unnecessary location.search dependency

  return { getInitialTab, getInitialFilters, initialQuery: getFilterFromURL("q", "") as string };
};
